import { useNewProjectService, RisksType } from 'domain/shared/newProject';
import useCustomerReportService from '../useCustomerReportService';
import { useEmployeeService, useEmployeeNamesService } from 'domain/shared/employee';
import { DATE_FORMAT, useDateService, useAsyncValue } from 'framework/utils';
import { EXTERNAL_USER_PREFIX } from 'domain/projects/newProject/risks/RiskOwnerDropdown';
import {
  generateBusinessOutcomesTable,
  generateCRTable,
  generateMilestonesTable,
  generateOldViewMilestonesTable,
  generateRiskTable,
} from '../mdEditor/utills';
import { useCallback, useMemo, useState } from 'react';
import { markdownToHtml, PlaceholderValues, replacePlaceholders } from 'framework/markdownEditor';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';

export const useCustomerReportTemplateData = (id: string, reportId?: string, defaultReportData: any = {}) => {
  const { getById } = useNewProjectService();
  const { getCustomerReportById } = useCustomerReportService();
  const { getFullNameWithoutMaiden } = useEmployeeService();
  const { getEmployeeNamesByAliases } = useEmployeeNamesService();
  const { getUserAlias } = useAuthenticatedUserService();

  const { getToday } = useDateService();
  const projectData = useAsyncValue(() => getById(id));
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = useCallback(() => setRefreshKey(k => k + 1), []);
  const reportData = useAsyncValue(() => {
    if (reportId) {
      return getCustomerReportById(reportId, {});
    } else return Promise.resolve(defaultReportData);
  }, [reportId, refreshKey]);

  const getAliasFullName = async (alias: string | undefined, defaultValue: string = '') => {
    if (!alias) return defaultValue;
    return getEmployeeNamesByAliases([alias])
      .then(response => getFullNameWithoutMaiden(response?.[0]?.nameEn))
      .catch(() => {
        return defaultValue;
      });
  };

  const placeholderValues: Nullable<PlaceholderValues> = useAsyncValue(async () => {
    if (!reportData || !projectData) return null;

    const processedRisks = await Promise.all(
      (reportData.risks || []).map(async (risk: RisksType) => {
        let riskOwnerName = '';

        if (risk.riskOwner) {
          const isExternal = risk.riskOwner.startsWith(EXTERNAL_USER_PREFIX);

          if (isExternal) {
            riskOwnerName = risk.riskOwner.slice(EXTERNAL_USER_PREFIX.length);
          } else {
            riskOwnerName = await getAliasFullName(risk.riskOwner, risk.riskOwner);
          }
        }

        return {
          ...risk,
          riskOwnerName,
        };
      }),
    );
    const currentUserAlias = await getUserAlias();
    const projectManagerName = await getAliasFullName(reportData.createdBy ?? currentUserAlias);

    const useTripleBraces = reportData.template ? /{{{.*milestonesList.*}}}/.test(reportData.template) : true;
    const milestonesTable = useTripleBraces
      ? generateMilestonesTable(reportData.milestones)
      : generateOldViewMilestonesTable(reportData.milestones);
    const businessOutcomesTable = generateBusinessOutcomesTable(reportData.businessOutcomes || []);
    const risksTable = generateRiskTable(processedRisks || []);
    const changeRequestsTable = generateCRTable(reportData.changeRequests || []);

    return {
      projectName: projectData.name,
      projectManager: projectManagerName,
      title: reportData.title,
      statusDate: reportData.generationDate
        ? reportData.generationDate
        : `${getToday().format(DATE_FORMAT.API_ONLY_DATE)}`,
      businessOutcomesList: businessOutcomesTable,
      milestonesList: milestonesTable,
      risksList: risksTable,
      changeRequestsList: changeRequestsTable,
    };
  }, [projectData, reportData]);

  const generateHtml = useCallback((template: string, placeholders: Nullable<PlaceholderValues>) => {
    if (!template || !placeholders) return undefined;
    const content = template || '';
    const contentWithValues = replacePlaceholders(content, placeholders);
    return markdownToHtml(contentWithValues);
  }, []);

  return useMemo(
    () => ({
      placeholders: placeholderValues,
      reportData,
      html: generateHtml(reportData?.template, placeholderValues),
      refetch,
    }),
    [generateHtml, placeholderValues, reportData, refetch],
  );
};
